import { useI18n } from '../utils/i18n';
import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Ticket, Hourglass } from 'lucide-react';
import apiService from '../services/apiService';
import { CalenderMarkersCalendarDisplay } from '../utils/calenderMarkerUtils';
import './CompactEventList.css';

const CompactEventList = ({ 
  events, 
  onEventClick, 
  refreshKey, 
  currentUserEmail = null,
  linkedCalendarEventIds: linkedCalendarEventIdsProp = null,
  calendarEventEventTicketStatus: _ignored = null, // backward compat guard if name changes
  calendarEventTicketStatus: calendarEventTicketStatusProp = null,
  plannedTicketEntries = []
}) => {
  const { getLocale, t } = useI18n();
  const [linkedCalendarEventIds, setLinkedCalendarEventIds] = useState([]);
  const [calendarEventTicketStatus, setCalendarEventTicketStatus] = useState({});
  const usePropsLinkedData = Array.isArray(linkedCalendarEventIdsProp) || (linkedCalendarEventIdsProp && typeof linkedCalendarEventIdsProp.length === 'number') || (calendarEventTicketStatusProp && typeof calendarEventTicketStatusProp === 'object');
  const [currentTime, setCurrentTime] = useState(new Date());

  // Get user's timezone automatically
  const getUserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

  const loadLinkedItems = async () => {
    try {
      console.log('📅 [COMPACT EVENT LIST] Loading linked items...');
      const response = await apiService.getLinkedItems();
      const linkedIds = response.linkedCalendarEventIds || [];
      const ticketStatus = response.calendarEventTicketStatus || {};
      console.log('📅 [COMPACT EVENT LIST] Linked calendar event IDs:', linkedIds);
      console.log('📅 [COMPACT EVENT LIST] Calendar event ticket status:', ticketStatus);
      setLinkedCalendarEventIds(linkedIds);

      // Enrichment: Falls projectId/subprojectId fehlen, versuche Details nachzuladen
      try {
        const missingIds = Object.keys(ticketStatus).filter(id => {
          const info = ticketStatus[id];
          return info && !info.projectId && !info.subprojectId;
        });
        // Begrenze parallele Nachladungen
        const toFetch = missingIds.slice(0, 25);
        if (toFetch.length > 0) {
          console.log('📅 [COMPACT EVENT LIST] Enriching project linkage for', toFetch.length, 'events');
          const results = await Promise.allSettled(toFetch.map(async (calendarEventId) => {
            try {
              const res = await apiService.getTicketByCalendarEvent(calendarEventId);
              return { calendarEventId, ticket: res.ticket || null };
            } catch (e) {
              return { calendarEventId, ticket: null };
            }
          }));
          results.forEach(r => {
            if (r.status === 'fulfilled' && r.value && r.value.ticket) {
              const { calendarEventId, ticket } = r.value;
              if (ticketStatus[calendarEventId]) {
                ticketStatus[calendarEventId].projectId = ticket.projectId || null;
                ticketStatus[calendarEventId].subprojectId = ticket.subprojectId || null;
              }
            }
          });
        }
      } catch (enrichErr) {
        console.warn('📅 [COMPACT EVENT LIST] Enrichment failed:', enrichErr);
      }

      setCalendarEventTicketStatus({ ...ticketStatus });
    } catch (error) {
      console.error('📅 [COMPACT EVENT LIST] Error loading linked items:', error);
      setLinkedCalendarEventIds([]);
      setCalendarEventTicketStatus({});
    }
  };

  // Load linked items when component mounts (only if not provided via props)
  useEffect(() => {
    if (!usePropsLinkedData) {
      console.log('📅 [COMPACT EVENT LIST] Component mounted, loading linked items...');
      loadLinkedItems();
    } else {
      // Seed local state from props for consistent usage
      try {
        if (Array.isArray(linkedCalendarEventIdsProp)) {
          setLinkedCalendarEventIds(linkedCalendarEventIdsProp);
        }
        if (calendarEventTicketStatusProp && typeof calendarEventTicketStatusProp === 'object') {
          setCalendarEventTicketStatus({ ...calendarEventTicketStatusProp });
        }
      } catch (_) {}
    }
  }, [usePropsLinkedData]);

  // Keep local state in sync when props change (important for instant updates from Dashboard)
  useEffect(() => {
    if (usePropsLinkedData) {
      try {
        if (Array.isArray(linkedCalendarEventIdsProp)) {
          setLinkedCalendarEventIds(linkedCalendarEventIdsProp);
        }
        if (calendarEventTicketStatusProp && typeof calendarEventTicketStatusProp === 'object') {
          setCalendarEventTicketStatus({ ...calendarEventTicketStatusProp });
        }
      } catch (_) {}
    }
  }, [usePropsLinkedData, linkedCalendarEventIdsProp, calendarEventTicketStatusProp, refreshKey]);

  // Load linked items when events list changes
  useEffect(() => {
    if (!usePropsLinkedData && events && events.length > 0) {
      console.log('📅 [COMPACT EVENT LIST] Events loaded, refreshing linked items...');
      loadLinkedItems();
    }
  }, [events, usePropsLinkedData]);

  // Refresh linked items when refreshKey changes (triggered by ticket updates)
  useEffect(() => {
    if (!usePropsLinkedData && refreshKey && events && events.length > 0) {
      console.log('📅 [COMPACT EVENT LIST] Refresh key changed, reloading linked items...');
      loadLinkedItems();
    }
  }, [refreshKey, usePropsLinkedData]);

  // Debug logging for linkedCalendarEventIds changes
  useEffect(() => {
    console.log('📅 [COMPACT EVENT LIST] LinkedCalendarEventIds updated:', linkedCalendarEventIds);
    console.log('📅 [COMPACT EVENT LIST] CalendarEventTicketStatus updated:', calendarEventTicketStatus);
    console.log('📅 [COMPACT EVENT LIST] Current events:', events.map(e => e.id));
  }, [linkedCalendarEventIds, calendarEventTicketStatus, events]);

  // Update current time every minute for time marker
  useEffect(() => {
    setCurrentTime(new Date());
    
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60 * 1000); // Update every minute

    return () => clearInterval(timeInterval);
  }, []);

  // Helper function to check if event is currently running
  const isEventCurrentlyRunning = (event) => {
    const now = currentTime;
    const eventStart = parseDateTime(event.start.dateTime);
    const eventEnd = parseDateTime(event.end.dateTime);
    
    return now >= eventStart && now <= eventEnd;
  };

  // Helper function to get current time position within an event (0-100%)
  const getCurrentTimePositionInEvent = (event) => {
    const now = currentTime;
    const eventStart = parseDateTime(event.start.dateTime);
    const eventEnd = parseDateTime(event.end.dateTime);
    
    if (now < eventStart || now > eventEnd) {
      return null; // Event is not currently running
    }
    
    const totalDuration = eventEnd - eventStart;
    const elapsed = now - eventStart;
    const position = (elapsed / totalDuration) * 100;
    
    return Math.max(0, Math.min(100, position));
  };

  // Build effective status including planned-only entries
  const getEffectiveStatusMap = () => {
    const base = (calendarEventTicketStatusProp && typeof calendarEventTicketStatusProp === 'object')
      ? { ...calendarEventTicketStatusProp }
      : { ...calendarEventTicketStatus };
    try {
      if (Array.isArray(plannedTicketEntries) && plannedTicketEntries.length > 0) {
        plannedTicketEntries.forEach(pe => {
          if (pe && pe.id && pe.__ticketInfo) {
            base[pe.id] = { ...pe.__ticketInfo };
          }
        });
      }
    } catch (_) {}
    return base;
  };

  const handleEventClick = async (event) => {
    const effectiveStatusForClick = getEffectiveStatusMap();
    if (!onEventClick) return;

    // 1) Bevorzugt: Direkt über vorab geladenen Status (vermeidet Fehlzuordnung bei Serien-IDs)
    try {
      const info = effectiveStatusForClick[event.id];
      const ticketIdFromStatus = info && (info.ticketId || null);
      if (ticketIdFromStatus != null) {
        try {
          // Hole das Ticket deterministisch per ID
          const resp = await apiService.getTicket(ticketIdFromStatus);
          const ticket = resp?.ticket || resp;
          if (ticket && ticket.id != null) {
            try { await apiService.markTicketAsViewed(ticket.id); } catch (_) {}
            onEventClick(ticket);
            return;
          }
        } catch (e) {
          // Fallback auf Lookup via Calendar-Event-ID
        }
      }
    } catch (_) {}

    // 1b) Direkter Klick auf geplantes Ticket ohne Graph-Event
    try {
      if (event.__ticket && event.__ticket.id != null) {
        try { await apiService.markTicketAsViewed(event.__ticket.id); } catch (_) {}
        onEventClick(event.__ticket);
        return;
      }
    } catch (_) {}

    // 2) Fallback: Server-Lookup über Calendar-Event-ID
    try {
      console.log(`📅 [COMPACT EVENT LIST] Finding ticket for calendar event ${event.id}`);
      const response = await apiService.getTicketByCalendarEvent(event.id);
      if (response.ticket) {
        console.log(`📅 [COMPACT EVENT LIST] Found ticket ${response.ticket.ticketNumber} for event ${event.id}`);
        try { await apiService.markTicketAsViewed(response.ticket.id); } catch (_) {}
        onEventClick(response.ticket);
        return;
      }
    } catch (error) {
      console.error('📅 [COMPACT EVENT LIST] Error finding ticket for calendar event:', error);
    }

    // 3) Letzter Fallback: mit Event-Daten öffnen (Graph-only oder unbekannt)
    onEventClick(event);
  };

  if ((!events || events.length === 0) && (!plannedTicketEntries || plannedTicketEntries.length === 0)) {
    return (
      <div className="dashboard-empty-state">
        <Calendar size={32} style={{ opacity: 0.3 }} />
        <p>{t('dashboard.noUpcomingEvents', 'Keine anstehenden Termine')}</p>
      </div>
    );
  }

  // Convert datetime string to proper Date object with timezone handling
  const parseDateTime = (dateTimeString) => {
    // FIXED: Treat the datetime as UTC and convert properly
    const utcDateTime = dateTimeString.endsWith('Z') ? dateTimeString : dateTimeString + 'Z';
    return new Date(utcDateTime);
  };

  // Extract ticket number reference from Graph event body (e.g., [TICKET:12345])
  const extractTicketNumberFromEvent = (eventBody) => {
    try {
      if (!eventBody || !eventBody.content) return null;
      const match = eventBody.content.match(/\[TICKET:([^\]]+)\]/);
      return match ? match[1] : null;
    } catch (_) {
      return null;
    }
  };

  // Build a quick lookup of completed ticket numbers for filtering Graph-only events
  const getCompletedTicketNumbers = () => {
    const completed = new Set();
    try {
      const effectiveStatus = calendarEventTicketStatusProp && typeof calendarEventTicketStatusProp === 'object' 
        ? calendarEventTicketStatusProp 
        : calendarEventTicketStatus;
      const byTicket = new Map();
      Object.values(effectiveStatus || {}).forEach(info => {
        try {
          if (!info || !info.ticketNumber) return;
          const key = String(info.ticketNumber);
          const arr = byTicket.get(key) || [];
          arr.push({ status: info.status, archived: !!info.archived });
          byTicket.set(key, arr);
        } catch (_) {}
      });
      byTicket.forEach((arr, ticketNumber) => {
        try {
          const nonArchived = arr.filter(x => !x.archived);
          if (nonArchived.length > 0) {
            // Nur als abgeschlossen markieren, wenn alle nicht-archivierten Einträge abgeschlossen sind
            if (nonArchived.every(x => x.status === 'abgeschlossen')) {
              completed.add(ticketNumber);
            }
          } else {
            // Fallback: keine nicht-archivierten Einträge vorhanden → alle (archivierten) müssen abgeschlossen sein
            if (arr.every(x => x.status === 'abgeschlossen')) {
              completed.add(ticketNumber);
            }
          }
        } catch (_) {}
      });
    } catch (_) {}
    return completed;
  };

  // Current user's email for assignment filtering
  const getCurrentUserEmail = () => {
    try {
      if (currentUserEmail) return currentUserEmail;
      const u = window.__currentUser || null;
      if (!u) return null;
      return typeof u === 'string' ? u : (u.userPrincipalName || u.mail || u.email || u.username || null);
    } catch (_) { return null; }
  };


  // Merge Graph events with planned-only pseudo events, then filter and sort
  const mergedEvents = Array.isArray(plannedTicketEntries) && plannedTicketEntries.length > 0
    ? [...events, ...plannedTicketEntries]
    : events;

  // Variant of filter that uses an explicit status map (includes planned-only entries)
  const filterEventsWithStatus = (list, statusMap) => {
    const completedTicketNumbers = getCompletedTicketNumbers();
    const currentUserEmailLower = (getCurrentUserEmail() || '').toLowerCase();
    return list.filter(event => {
      const ticketInfo = statusMap[event.id];
      if (ticketInfo) {
        if (ticketInfo.status === 'abgeschlossen') {
          try { console.log(`📅 [COMPACT EVENT LIST] Filtering out event ${event.id} - linked ticket ${ticketInfo.ticketNumber} is completed`); } catch (_) {}
          return false;
        }
        const assignedTo = (ticketInfo.assignedTo || '').toLowerCase();
        if (!currentUserEmailLower || !assignedTo || assignedTo !== currentUserEmailLower) {
          try { console.log(`📅 [COMPACT EVENT LIST] Filtering out event ${event.id} - assigned to other user (${ticketInfo.assignedTo})`); } catch (_) {}
          return false;
        }
      }
      const referencedTicketNumber = extractTicketNumberFromEvent(event.body);
      if (referencedTicketNumber && completedTicketNumbers.has(String(referencedTicketNumber))) {
        try { console.log(`📅 [COMPACT EVENT LIST] Filtering out event ${event.id} - body references completed ticket ${referencedTicketNumber}`); } catch (_) {}
        return false;
      }
      return true;
    });
  };

  const filteredEvents = filterEventsWithStatus(mergedEvents, getEffectiveStatusMap());
  const sortedEvents = [...filteredEvents].sort((a, b) => 
    parseDateTime(a.start.dateTime) - parseDateTime(b.start.dateTime)
  );

  // Helper function to check if two events overlap
  const eventsOverlap = (event1, event2) => {
    const start1 = parseDateTime(event1.start.dateTime);
    const end1 = parseDateTime(event1.end.dateTime);
    const start2 = parseDateTime(event2.start.dateTime);
    const end2 = parseDateTime(event2.end.dateTime);
    
    return start1 < end2 && start2 < end1;
  };

  // Group overlapping events
  const groupOverlappingEvents = (events) => {
    const groups = [];
    const used = new Set();

    for (let i = 0; i < events.length; i++) {
      if (used.has(i)) continue;

      const group = [events[i]];
      used.add(i);

      // Find all events that overlap with any event in the current group
      let foundOverlap = true;
      while (foundOverlap) {
        foundOverlap = false;
        for (let j = 0; j < events.length; j++) {
          if (used.has(j)) continue;

          // Check if this event overlaps with any event in the current group
          if (group.some(groupEvent => eventsOverlap(groupEvent, events[j]))) {
            group.push(events[j]);
            used.add(j);
            foundOverlap = true;
          }
        }
      }

      groups.push(group);
    }

    return groups;
  };

  // Find all event groups and categorize them
  const now = new Date();
  const eventGroups = groupOverlappingEvents(sortedEvents);
  
  const pastGroups = [];
  let currentGroup = null;
  const futureGroups = [];

  for (const group of eventGroups) {
    // Check if any event in the group is currently running
    const hasCurrentEvent = group.some(event => {
      const startTime = parseDateTime(event.start.dateTime);
      const endTime = parseDateTime(event.end.dateTime);
      return startTime <= now && endTime > now;
    });

    if (hasCurrentEvent) {
      currentGroup = group;
    } else {
      // Check if this group is in the past or future
      const groupEndTime = Math.max(...group.map(event => parseDateTime(event.end.dateTime)));
      const groupStartTime = Math.min(...group.map(event => parseDateTime(event.start.dateTime)));
      
      if (groupEndTime <= now) {
        // Past group - all events have ended
        pastGroups.push(group);
      } else if (groupStartTime > now) {
        // Future group - all events start in the future
        futureGroups.push(group);
      }
    }
  }

  const formatTime = (dateTimeString) => {
    // FIXED: Treat the datetime as UTC and convert properly
    const utcDateTime = dateTimeString.endsWith('Z') ? dateTimeString : dateTimeString + 'Z';
    const date = new Date(utcDateTime);
    const userTimeZone = getUserTimeZone();
    
    return date.toLocaleTimeString(getLocale(), { 
      hour: '2-digit', 
      minute: '2-digit',
      timeZone: userTimeZone
    });
  };

  const getDateTag = (dateTimeString) => {
    const utcDateTime = dateTimeString.endsWith('Z') ? dateTimeString : dateTimeString + 'Z';
    const eventDate = new Date(utcDateTime);
    const today = new Date();
    
    // Reset time to compare dates only
    today.setHours(0, 0, 0, 0);
    const eventDay = new Date(eventDate);
    eventDay.setHours(0, 0, 0, 0);
    
    const diffTime = eventDay - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return null; // Today - no tag
    if (diffDays === 1) return 'Morgen';
    if (diffDays === 2) return 'Übermorgen';
    return null;
  };

  const formatEventTitle = (title) => {
    if (title.length > 25) {
      return title.substring(0, 25) + '...';
    }
    return title;
  };

  // Localized duration label for the left strip (rounded to 0.5h)
  const formatDurationHoursLabel = (start, end) => {
    try {
      const startDate = start instanceof Date ? start : parseDateTime(start);
      const endDate = end instanceof Date ? end : parseDateTime(end);
      const ms = Math.max(0, endDate - startDate);
      const hours = ms / (1000 * 60 * 60);
      const rounded = Math.round(hours * 2) / 2; // steps of 0.5h
      const isInt = Math.abs(rounded - Math.round(rounded)) < 1e-9;
      const loc = (getLocale && typeof getLocale === 'function' ? String(getLocale()) : 'en-GB').toLowerCase();
      const lang = loc.startsWith('de') ? 'de' : (loc.startsWith('es') ? 'es' : 'en');

      let numberStr;
      if (lang === 'en') {
        numberStr = isInt ? String(Math.round(rounded)) : String(rounded.toFixed(1)).replace(/\.0$/, '');
      } else {
        // de/es use comma as decimal separator
        const val = isInt ? Math.round(rounded).toString() : rounded.toFixed(1).replace(/\.0$/, '');
        numberStr = val.replace('.', ',');
      }

      // Einheit entfernen: nur Zahlenwert anzeigen
      return `${numberStr}`;
    } catch (_) {
      return '';
    }
  };

  // Create display groups - show all groups for today (past, current, future)
  const displayGroups = [];
  
  // Add past groups
  pastGroups.forEach(group => {
    displayGroups.push({ group, type: 'past' });
  });
  
  // Add current group
  if (currentGroup) {
    displayGroups.push({ group: currentGroup, type: 'current' });
  }
  
  // Add future groups
  futureGroups.forEach(group => {
    displayGroups.push({ group, type: 'upcoming' });
  });

  // Helpers for free time cards
  const getGroupStartTime = (group) => {
    const times = group.map(e => parseDateTime(e.start.dateTime).getTime());
    return new Date(Math.min(...times));
  };

  const getGroupEndTime = (group) => {
    const times = group.map(e => parseDateTime(e.end.dateTime).getTime());
    return new Date(Math.max(...times));
  };

  const isSameLocalDay = (a, b) => {
    if (!a || !b) return false;
    const d1 = new Date(a);
    const d2 = new Date(b);
    return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
  };

  const isTodayLocal = (d) => {
    const today = new Date();
    return isSameLocalDay(d, today);
  };

  const formatTimeFromDate = (date) => {
    try {
      const userTimeZone = getUserTimeZone();
      return date.toLocaleTimeString(getLocale(), { hour: '2-digit', minute: '2-digit', timeZone: userTimeZone });
    } catch (_) {
      return '';
    }
  };

  // Build render items including free-time cards between groups (desktop only via CSS)
  const itemsToRender = [];
  for (let i = 0; i < displayGroups.length; i++) {
    const current = displayGroups[i];
    itemsToRender.push({ kind: 'group', data: current, key: `group-${i}` });
    if (i < displayGroups.length - 1) {
      const next = displayGroups[i + 1];
      const endCurrent = getGroupEndTime(current.group);
      const startNext = getGroupStartTime(next.group);
      const hasGap = startNext > endCurrent;
      // Helper: check if ANY event (even ausgeblendet durch Filter) liegt zwischen den Gruppen
      const anyEventBetween = (() => {
        try {
          const startMs = endCurrent.getTime();
          const endMs = startNext.getTime();
          for (const ev of mergedEvents || []) {
            try {
              const s = parseDateTime(ev.start.dateTime).getTime();
              const e = parseDateTime(ev.end.dateTime).getTime();
              // Überlappt der Zeitraum mit einem beliebigen Event?
              if (s < endMs && e > startMs) {
                return true;
              }
            } catch (_) {}
          }
        } catch (_) {}
        return false;
      })();

      // Only show gaps that are on the same local day and that day is today, and there are truly no events in-between
      if (hasGap && isSameLocalDay(endCurrent, startNext) && isTodayLocal(endCurrent) && !anyEventBetween) {
        itemsToRender.push({ kind: 'free', data: { start: endCurrent, end: startNext }, key: `gap-${i}` });
      }
    }
  }

  // Render function for individual events
  const renderEvent = (event, groupType, isFirstInGroup, isLastInGroup, groupSize) => {
    const dateTag = getDateTag(event.start.dateTime);
    const effectiveLinkedIds = Array.isArray(linkedCalendarEventIdsProp) ? linkedCalendarEventIdsProp : linkedCalendarEventIds;
    const effectiveStatusForRender = (calendarEventTicketStatusProp && typeof calendarEventTicketStatusProp === 'object') 
      ? calendarEventTicketStatusProp 
      : calendarEventTicketStatus;
    const isLinked = Array.isArray(effectiveLinkedIds) && effectiveLinkedIds.includes(event.id);
    const ticketInfo = effectiveStatusForRender[event.id];
    const isWaitingTicket = ticketInfo && ticketInfo.status === 'wartend';
    
    return (
      <div 
        key={event.id} 
        className={`dashboard-event-item ${groupType} ${isLinked ? 'linked' : ''} ${isWaitingTicket ? 'waiting' : ''} ${groupSize > 1 ? 'grouped' : ''} ${isFirstInGroup ? 'first-in-group' : ''} ${isLastInGroup ? 'last-in-group' : ''}`}
        onClick={() => handleEventClick(event)}
        style={{ 
          cursor: 'pointer',
          position: 'relative'
        }}
      >
        <div className="duration-strip">
          <div className="duration-text">{formatDurationHoursLabel(event.start.dateTime, event.end.dateTime)}</div>
        </div>
        <div className="event-indicator">
          {groupType === 'current' ? '●' : groupType === 'past' ? '◯' : groupType === 'next' ? '○' : '◦'}
        </div>
        <div className="event-info">
          <div className="event-title">
            {formatEventTitle(event.subject)}
          </div>
          <div className="event-time">
            <Clock size={12} />
            {formatTime(event.start.dateTime)} - {formatTime(event.end.dateTime)}
            {dateTag && isFirstInGroup && (
              <span className="date-tag">{dateTag}</span>
            )}
          </div>
        </div>
        {(() => {
          try {
            const provider = String(event?.onlineMeetingProvider || '').toLowerCase();
            const joinUrl = event?.onlineMeeting?.joinUrl || event?.onlineMeetingUrl || null;
            const isTeams = Boolean((event?.isOnlineMeeting && provider.includes('teams')) || (joinUrl && /https?:\/\/(.*\.)?teams\.microsoft\.com/i.test(joinUrl)));
            if (!isTeams) return null;
            return (
              <div
                style={{ 
                  position: 'absolute',
                  top: '5px',
                  right: '8px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1px',
                  zIndex: 30,
                  pointerEvents: 'none',
                  backgroundColor: 'var(--color-glass-90)',
                  borderRadius: '3px',
                  boxShadow: '0 1px 3px var(--color-shadow-30)'
                }}
                title={t('eventDetail.teamsMarker', 'Teams-Besprechung')}
              >
                <span 
                  style={{ fontSize: '12px', lineHeight: '12px', display: 'block', textAlign: 'center' }}
                >
                  🎥
                </span>
              </div>
            );
          } catch (_) { return null; }
        })()}
        {isLinked && (
          <div style={{ 
            position: 'absolute',
            bottom: '8px',
            right: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            {(() => {
              // Schwarzes "P" statt Ticket-Icon, wenn verknüpftes Ticket zu Vorgang/Teilvorgang gehört
              const isProjectLinked = !!(ticketInfo && (ticketInfo.projectId || ticketInfo.subprojectId));
              if (isProjectLinked) {
                return (
                  <div
                    title="Vorgangs-Ticket"
                    style={{
                      width: 16,
                      height: 16,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 800,
                      color: 'var(--color-text-black)'
                    }}
                  >
                    P
                  </div>
                );
              }
              return (
                <Ticket 
                  size={16} 
                  style={{ 
                    color: isWaitingTicket ? 'var(--color-text-muted)' : 'var(--color-primary)' 
                  }} 
                  title={t('common.linkedToTicket', 'Mit Ticket verknüpft')} 
                />
              );
            })()}
            {isWaitingTicket && (
              <Hourglass 
                size={14} 
                style={{ 
                  color: 'var(--color-text-muted)' 
                }} 
                title="Ticket wartet"
              />
            )}
          </div>
        )}
        
        {/* Calendar Markers - show if ticket has markers */}
        {isLinked && ticketInfo && ticketInfo.calenderMarker && (
          <CalenderMarkersCalendarDisplay 
            markers={ticketInfo.calenderMarker} 
            position={{ top: '5px', right: '8px' }}
          />
        )}

        {/* Current time marker - show if event is currently running */}
        {isEventCurrentlyRunning(event) && getCurrentTimePositionInEvent(event) !== null && (
          <div 
            className="current-time-marker-vertical"
            style={{
              position: 'absolute',
              left: `${10 + (getCurrentTimePositionInEvent(event) * 0.9)}%`,
              top: '0',
              bottom: '0',
              width: '2px',
              backgroundColor: 'var(--color-danger)',
              zIndex: 1000,
              boxShadow: '0 0 4px var(--color-danger-shadow-50)',
              borderRadius: '1px'
            }}
          >


          </div>
        )}

      </div>
    );
  };

  return (
    <div className="dashboard-events-list compact horizontal">
      {itemsToRender.map((item) => {
        if (item.kind === 'group') {
          const { group, type } = item.data;
          const isOverlappingGroup = group.length > 1;
          return (
            <div 
              key={item.key}
              className={`event-group ${isOverlappingGroup ? 'overlapping' : ''}`}
              style={{
                border: isOverlappingGroup ? '2px solid var(--color-primary)' : 'none',
                borderRadius: isOverlappingGroup ? '8px' : '0',
                padding: isOverlappingGroup ? '4px' : '0',
                margin: isOverlappingGroup ? '2px' : '0',
                backgroundColor: isOverlappingGroup ? 'var(--color-primary-tint-05)' : 'transparent'
              }}
            >
              {group.map((event, eventIndex) => 
                renderEvent(
                  event, 
                  type, 
                  eventIndex === 0, 
                  eventIndex === group.length - 1, 
                  group.length
                )
              )}
            </div>
          );
        }
        // free-time card
        const { start, end } = item.data;
        return (
          <div key={item.key} className="event-group">
            <div 
              className="dashboard-event-item free-time-card"
              style={{ cursor: 'default' }}
            >
              <div className="duration-strip">
                <div className="duration-text">{formatDurationHoursLabel(start, end)}</div>
              </div>
              <div className="event-info">
                <div className="event-title">{t('dashboard.freeTime', 'Freie Zeit')}</div>
                <div className="event-time">
                  <Clock size={12} />
                  {formatTimeFromDate(start)} - {formatTimeFromDate(end)}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CompactEventList; 